/**
 * @file       Date input directive for MM/DD/YYYY format
 * @version    1.0.0
 * @author     Boris Veits
 * @copyright  Copyright (c) 2018, MindShare HDV LLC.  All rights reserved.
 *             Property of MindShare HDV LLC.  This file cannot be used or 
 *             altered in anyway without expressed written permission from 
 *             MindShare HDV LLC.
 */
import { Directive, ElementRef, HostListener, Optional } from '@angular/core';
import { NgControl } from '@angular/forms';

@Directive
({
    selector: '[appDateInput]',
    standalone: false
})

/**
 * @desc   - directive for allowing only numbers in MM/DD/YYYY format
 */
class DateInputDirective 
{
    private updating: boolean;

    /**
     * @desc   - directive constructor
     * @access - public
     * @param  - el : ElementRef
     * @param  - ngControl : NgControl (optional)
     * @return - 
     */
    constructor
    (
        private el: ElementRef,
        @Optional() private ngControl: NgControl
    ) 
    {
        this.updating = false;
    }

    //__________________ P R I V A T E  M E T H O D S __________________________

    /**
     * @desc - format the date digits
     * @access - private
     * @param  - digits : string - the digits to format
     * @returns - string
     */
    private formatDateDigits
    (
        digits: string
    ) : string
    {
        let month;
        let day;
        let year;
        let monthNum;
        let dayNum;
        let result;

        month = digits.substring(0, 2);
        day = digits.substring(2, 4);
        year = digits.substring(4, 8);

        if (month.length === 2) 
        {
            monthNum = Number(month);
            if (monthNum < 1 || monthNum > 12) 
            {
                month = month.substring(0, 1);
            }
        }

        if (day.length === 2) 
        {
            dayNum = Number(day);
            if (dayNum < 1 || dayNum > 31) 
            {
                day = day.substring(0, 1);
            }
        }

        result = month;
        if (digits.length > 2) 
        {
            result += '/' + day;
        }
        if (digits.length > 4) 
        {
            result += '/' + year;
        }

        return result;
    }

    //___________________ P U B L I C  M E T H O D S ___________________________

    /**
     * @desc  - handle the user typing in the field and format as MM/DD/YYYY
     * @access - public
     * @param  - event: InputEvent - the input event from the field
     * @return - 
     */
    @HostListener('input', ['$event'])
    onInput
    (
        event: InputEvent
    ) 
    {
        let input;
        let inputValue;
        let cleanedInput;
        let formattedValue;
        let dateParts;
        let monthStr;
        let dayStr;
        let yearStr;
        let month;
        let day;
        let year;
        let dateObj;

        if (!this.updating)
        {
            input = event.target as HTMLInputElement;
            inputValue = input.value;

            cleanedInput = inputValue.replace(/\D/g, '');

            formattedValue = this.formatDateDigits(cleanedInput);

            input.value = formattedValue;

            if (this.ngControl?.control) 
            {
                if (formattedValue.length === 0) 
                {
                    this.updating = true;
                    this.ngControl.control.setValue(null);
                    this.updating = false;
                }
                else if (formattedValue.length === 10) 
                {
                    dateParts = formattedValue.split('/');
                    monthStr = dateParts[0];
                    dayStr = dateParts[1];
                    yearStr = dateParts[2];
                    month = parseInt(monthStr, 10);
                    day = parseInt(dayStr, 10);
                    year = parseInt(yearStr, 10);
                    dateObj = new Date(year, month - 1, day);
                    this.updating = true;
                    this.ngControl.control.setValue(dateObj);
                    this.updating = false;
                }
            }
        }
    }
}

export { DateInputDirective };
